import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, from, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, filter, map, switchMap, take } from "rxjs/operators";
import { Router } from '@angular/router';
import { AlertService, IAlertType } from "./alert.service";
import { CrmStoreService } from "./crm-store.service";
import { environment, environment as env} from "../../../environments/environment";
import { StatusResponse, Tests } from "../interfaces";
import { getTenantStorage, removeTenantStorage, setTenantStorage } from "../utils/tenant-context";

@Injectable({
  providedIn: 'root'
})

export class AuthService {
  private rmsEndpoint = this.localDevBasePath('/v1/rest', env.rest);
  private authEndpoint = this.localDevBasePath('/v1/auth', env.auth);
  private readonly loginDebugReasonKey = 'pps_auth_debug_reason';
  checker: boolean = false;
  success: boolean = false;

  isRefreshing = false;
  readonly refreshToken$ = new BehaviorSubject<string | null>(null);

  devMode: boolean;
  crmMode: boolean;

  constructor(
    private http: HttpClient,
    public router: Router,
    public alertDispatcher: AlertService,
    public crmConnector: CrmStoreService,
  ) {
    this.devMode = env.stages.dev;
    this.crmMode = environment.externalCrm;
  }

  private buildBaseUrl(cfg: { apiUrl: string; protocol: string; port?: number }) {
    const port = cfg.port && cfg.port !== 80 && cfg.port !== 443 ? `:${cfg.port}` : '';
    return `${cfg.protocol}://${cfg.apiUrl}${port}`;
  }

  private localDevBasePath(proxyPath: string, cfg: { apiUrl: string; protocol: string; port?: number }) {
    if (!env.production && env.stages?.dev && this.isLocalDevHost()) {
      return proxyPath;
    }
    return this.buildBaseUrl(cfg) + proxyPath;
  }

  private isLocalDevHost(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    const host = window.location.hostname.toLowerCase();
    return host === 'localhost' || host === '127.0.0.1';
  }

  // Sign-in
  signIn(username: string, userpass: string): Observable<any> {
    this.clearLoginDebugReason();
    this.resetLocalStorage()

    return this.http.post<any>(this.authEndpoint + '/login', {
      email: username,
      password: userpass,
      remember: true
    }).pipe(
      switchMap(res => from(this.handleLoginSuccess(res, username, userpass))),
      catchError((error: HttpErrorResponse) => {
        this.success = false;
        this.setLoginDebugReason('login_error', error.message || 'Login request failed.', {
          status: error.status,
          url: error.url ?? this.authEndpoint + '/login'
        });
        this.router.navigate(['/login']);
        return this.handleError(error);
      })
    );
  }

  private async handleLoginSuccess(res: any, username: string, userpass: string) {
    if (this.devMode) console.log('authss 42 - Resultat: ' + (res.user?.display_name ?? ''));
    this.checker = true;
    setTenantStorage('access_token', res.access_token);
    setTenantStorage('refresh_token', res.refresh_token);
    setTenantStorage('crm_token', '');
    setTenantStorage('user_id', res.user?.id ?? '');
    setTenantStorage('staff_id', res.user?.staff_id?.toString() ?? '');
    setTenantStorage('first_name', res.user?.display_name ?? '');
    setTenantStorage('last_name', '');

    // Nur im Speicher halten, nicht im sessionStorage: dort lag das Passwort
    // im Klartext und wurde vom System-Check der Login-Seite erneut eingespielt.
    const crmUser = res.user?.crm_username || res.user?.display_name || '';
    this.crmConnector.setReconnectCredentials(crmUser, userpass);

    if (this.crmMode) {
      try {
        await this.crmConnector.crmSignIn(crmUser, userpass);
        if (this.devMode) console.log('authss 53 - CRM Login ff ');
      } catch (error) {
        if (this.devMode) console.warn('authss 56 - CRM login unavailable, continuing with limited mode', error);
        this.alertDispatcher.show({
          message: 'CRM/CMS aktuell nicht erreichbar. Login bleibt aktiv, CRM-Funktionen sind eingeschraenkt.',
          clrAlertType: IAlertType.WARNING
        }, 7000);
      }
    }

    this.success = true;
    const returnUrl = this.router.parseUrl(this.router.url).queryParams['returnUrl'];
    await this.router.navigateByUrl(returnUrl ?? '/dashboard/pps');

    return res;
  }

  ping(): Observable<boolean> {
    if (!this.isAccessTokenExpired()) {
      return this.doPing();
    }

    // Access token expired: try the refresh token before giving up the session.
    const refreshToken = getTenantStorage('refresh_token');
    if (!refreshToken) {
      this.redirectToLogin('token_expired', 'Session expired. Please login again.', {
        expiresAt: this.getAccessTokenExpiry()
      });
      this.checker = false;
      return of(false);
    }

    if (this.isRefreshing) {
      return this.waitForRefresh().pipe(switchMap(() => this.doPing()));
    }

    this.isRefreshing = true;
    this.refreshToken$.next(null);

    return this.refreshAccessToken().pipe(
      switchMap(newToken => {
        this.isRefreshing = false;
        this.refreshToken$.next(newToken);
        return this.doPing();
      }),
      catchError(refreshError => {
        this.isRefreshing = false;
        this.redirectToLogin('token_expired', 'Session expired. Please login again.', {
          expiresAt: this.getAccessTokenExpiry(),
          refreshError: this.formatHttpError(refreshError)
        });
        this.checker = false;
        return of(false);
      })
    );
  }

  private doPing(): Observable<boolean> {
    return this.http.get<Tests>(this.rmsEndpoint + '/ping')
      .pipe(
        map((response: Tests) => {
          this.checker = response.object === true;
          return response.object === true;
        }),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 400 || error.status === 401) {
            this.redirectToLogin('ping_unauthorized', 'Session expired. Please login again.', {
              status: error.status,
              url: error.url ?? this.rmsEndpoint + '/ping'
            });
            this.checker = false;
          }
          return of(false);
        })
      );
  }

  getAccessToken() {
    return getTenantStorage('access_token');
  }

  refreshAccessToken(): Observable<string> {
    const refreshToken = getTenantStorage('refresh_token');
    if (!refreshToken) {
      return throwError(() => new Error('no_refresh_token'));
    }
    return this.http.post<any>(this.authEndpoint + '/refresh', { refresh_token: refreshToken }).pipe(
      map(res => {
        setTenantStorage('access_token', res.access_token);
        setTenantStorage('refresh_token', res.refresh_token);
        return res.access_token as string;
      })
    );
  }

  waitForRefresh(): Observable<string> {
    return this.refreshToken$.pipe(
      filter((token): token is string => token !== null),
      take(1)
    );
  }

  hasAccessToken(): boolean {
    return !!this.getAccessToken() && !this.isAccessTokenExpired();
  }

  isAdmin(): boolean {
    const token = this.getAccessToken();
    const payload = token ? this.decodeJwtPayload(token) : null;
    return payload?.['is_admin'] === true;
  }

  getAccessTokenExpiry(): number | null {
    const token = this.getAccessToken();
    if (!token) {
      return null;
    }

    const payload = this.decodeJwtPayload(token);
    const exp = payload?.['exp'];
    if (typeof exp === 'number') {
      return exp;
    }
    return null;
  }

  isAccessTokenExpired(leewaySeconds: number = 30): boolean {
    const exp = this.getAccessTokenExpiry();
    if (!exp) {
      return true;
    }

    const nowSeconds = Math.floor(Date.now() / 1000);
    return exp <= (nowSeconds + leewaySeconds);
  }

  healthCheck() {
    return this.http.get<Tests>(this.rmsEndpoint + '/ping')
  }

  rmsStatus() {
    return this.http.get<StatusResponse>(this.rmsEndpoint + '/status').pipe(
      catchError(() => of(null))
    );
  }

  authHealthCheck() {
    const endpoint = this.localDevBasePath('/healthz', env.auth);
    return this.http.get(endpoint, { responseType: 'text' }).pipe(
      map((res: string) => res.toLowerCase() === 'ok'),
      catchError(() => of(false))
    );
  }

  cmsAuthHealthCheck(): Observable<boolean> {
    if (!this.crmMode) {
      return of(false);
    }

    return this.crmConnector.crmHealthCheck();
  }

  doLogout() {
    const refreshToken = getTenantStorage('refresh_token');

    if (!refreshToken) {
      this.resetLocalStorage();
      this.handleLogoutWithoutRefreshToken();
      return;
    }

    this.http.post<Tests>(this.authEndpoint + '/logout', { refresh_token: refreshToken })
      .subscribe({
        next: (res: Tests | null) => {
          if (res?.hasErrors === false) {
            this.resetLocalStorage();
            this.handleLogoutSuccess();
          } else {
            this.resetLocalStorage();
            this.handleLogoutFailure('Logout failed on server.');
          }
        },
        error: () => {
          this.resetLocalStorage();
          this.handleLogoutFailure('Logout request failed.');
        }
      });
  }

  resetLocalStorage() {
    this.crmConnector.clearReconnectCredentials();
    removeTenantStorage('access_token');
    removeTenantStorage('refresh_token');
    removeTenantStorage('user_id');
    removeTenantStorage('staff_id');
    removeTenantStorage('first_name');
    removeTenantStorage('last_name');
    removeTenantStorage('is_admin');
    removeTenantStorage('crm_token');
    localStorage.removeItem('theme');
    localStorage.removeItem('pdfjs.history');
  }

  handleLogoutSuccess() {
    this.setLoginDebugReason('logout_success', 'Logout completed successfully.');
    this.alertDispatcher.alertConfig.message = 'Logout successful! :) ';
    this.alertDispatcher.alertConfig.clrAlertType = IAlertType.SUCCESS;
    this.router.navigate(['/login']).catch(err => {
      if (this.devMode) console.error('Navigation error:', err);
    });
  }

  handleLogoutFailure(message: string) {
    this.setLoginDebugReason('logout_failure', message);
    this.alertDispatcher.alertConfig.message = message;
    this.alertDispatcher.alertConfig.clrAlertType = IAlertType.DANGER;

    this.router.navigate(['/login']).catch(err => {
      if (this.devMode) console.error('Navigation error:', err);
    });
  }

  redirectToLogin(reason: string, message: string, details?: Record<string, unknown>, alertType: IAlertType = IAlertType.WARNING) {
    this.setLoginDebugReason(reason, message, details);
    const returnUrl = this.router.url !== '/login' ? this.router.url : undefined;
    this.resetLocalStorage();
    this.alertDispatcher.alertConfig.message = message;
    this.alertDispatcher.alertConfig.clrAlertType = alertType;
    this.router.navigate(['/login'], returnUrl ? { queryParams: { returnUrl } } : {}).catch(err => {
      if (this.devMode) console.error('Navigation error:', err);
    });
  }

  private handleLogoutWithoutRefreshToken() {
    this.setLoginDebugReason('logout_without_refresh_token', 'Manual logout completed locally because no refresh token was available.');
    this.alertDispatcher.alertConfig.message = 'Logout successful! :) ';
    this.alertDispatcher.alertConfig.clrAlertType = IAlertType.SUCCESS;
    this.router.navigate(['/login']).catch(err => {
      if (this.devMode) console.error('Navigation error:', err);
    });
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Unknown error!';
    if (error.error instanceof ErrorEvent) {
      errorMessage = `Client-side error: ${error.error.message}`;
    } else {
      errorMessage = `Server-side error code: ${error.status} - message: ${error.message}`;
    }

    this.alertDispatcher.show({
      message: errorMessage,
      clrAlertType: IAlertType.DANGER
    }, 5000);

    return throwError(() => new Error(errorMessage));
  }

  formatHttpError(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const body = typeof error.error === 'string' ? error.error : JSON.stringify(error.error ?? {});
      return `status=${error.status} url=${error.url ?? ''} message=${error.message} body=${body}`;
    }
    if (error instanceof Error) {
      return error.message;
    }
    return String(error);
  }

  private decodeJwtPayload(token: string): Record<string, unknown> | null {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    try {
      const payload = parts[1]
        .replace(/-/g, '+')
        .replace(/_/g, '/');
      const decoded = atob(payload.padEnd(payload.length + (4 - (payload.length % 4)) % 4, '='));
      return JSON.parse(decoded);
    } catch {
      return null;
    }
  }

  getLoginDebugReason():
    | { reason: string; message: string; details?: Record<string, unknown>; at: string }
    | null {
    const raw = localStorage.getItem(this.loginDebugReasonKey);
    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  clearLoginDebugReason() {
    localStorage.removeItem(this.loginDebugReasonKey);
  }

  setLoginDebugReason(reason: string, message: string, details?: Record<string, unknown>) {
    if (!this.devMode) {
      return;
    }

    localStorage.setItem(this.loginDebugReasonKey, JSON.stringify({
      reason,
      message,
      details,
      at: new Date().toISOString()
    }));
  }
}
